import { Injectable } from '@angular/core';
import { Profile } from '@model/profile.model';
import { User } from '@model/user.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { matchRule } from '@core';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    activeProfile$: BehaviorSubject<Profile>;
    profiles$: BehaviorSubject<Profile[]>;
    user$: BehaviorSubject<User>;
    _user$: Observable<User>;

    private _profilesNumber: number = 0;
    private _hasActiveProfile: boolean = false;

    constructor() {
        this.activeProfile$ = new BehaviorSubject(null);
        this.profiles$ = new BehaviorSubject([]);
        this.user$ = new BehaviorSubject(null);

        this._user$ = this.user$.asObservable().pipe(tap((user) => {
            this.activeProfile$.next(user.activeProfile);
            this.profiles$.next(user.profiles);
            this._profilesNumber = user.profiles.length;
            this._hasActiveProfile = !!user.activeProfile;
        }))
    }

    // SETTER / GETTER

    get profilesNumber() {
        return this._profilesNumber;
    }

    get hasActiveProfile() {
        return this._hasActiveProfile;
    }

    /**
    * fetch user in service
    * @param user user object to be consumed
    */
    addUser(user: any = {}) {
        this.user$.next(new User(user));
        this._user$.subscribe();

        return this.user$.getValue();
    }

    /**
     * activateProfile
     * @param id profile id to activate
     */
    activateProfile(id: number) {
        this.activeProfile$.next(this.getProfileById(id));
        this._hasActiveProfile = true;
    }

    /**
     * get profile by id from list
     * @param id profile id to search
     */
    getProfileById(id: number) {
        return this.profiles$.getValue().find((profile) => profile.id === id);
    }

    /**
     * get profile by name from list
     * @param name profile name to search
     */
    getProfileByName(name: string) {
        return this.profiles$.getValue().find((profile) => matchRule(profile.name, name));
    }

    /**
     * return true if profile is enable
     * @param name profile name to check
     */
    isProfileEnable(name: string) {
        return this.activeProfile$.getValue().name == name;
    }

    /**
     * return true if permission is enabled for a defined operation default is 'all' means write and read
     * @param name permission name to check
     * @param operationName operation "all", "write", "read"
     */
    isPermissionEnable(name: string, operationName?: string) {
        const activeProfile = this.activeProfile$.getValue();
        return activeProfile && activeProfile.isPermissionEnable ? activeProfile.isPermissionEnable(name, operationName) : false;
    }

    // reset user by cleaning active profile, profiles and user informations
    resetUser() {
        this.user$.next(null);
        this.activeProfile$.next(null);
        this._hasActiveProfile = false;
        this.profiles$.next([]);
    }

    // reset profile removing active profile
    resetProfile() {
        this.activeProfile$.next(null);
        this._hasActiveProfile = false;
    }
}